import { useCallback, useMemo, type PropsWithChildren } from "react";

import { useJsonLocalStorage } from "@/shared/hooks/useJsonLocalStorage";

import {
  FormatContext,
  formatContextDefaultValue,
  type FormatContextFormatLocaleOption,
  type FormatContextTimezoneOption,
} from "./format-context";

const TIMEZONE_STORAGE_KEY = "admin-timezone-preference";
const FORMAT_LOCALE_STORAGE_KEY = "admin-format-locale-preference";

const detectBrowserLocale = () => {
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language;
  }
  return formatContextDefaultValue.locale;
};

const detectBrowserTimezone = () => {
  if (typeof window !== "undefined" && typeof Intl !== "undefined") {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone) {
        return timezone;
      }
    } catch {
      // ignore timezone resolution failures
    }
  }
  return formatContextDefaultValue.timezoneCode;
};

const buildTimezoneOptions = (): FormatContextTimezoneOption[] => {
  const localTimezone = detectBrowserTimezone();
  return [
    { id: "utc", code: "UTC", label: "UTC" },
    { id: "local", code: localTimezone, label: "Local" },
  ];
};

// ISO-style dates (2030-12-31) on a 24-hour clock, with English month names and
// US-style number grouping. en-CA is the English locale whose short date is
// year-month-day; `-u-hc-h23` forces the 24-hour cycle it would otherwise drop.
const ISO_LOCALE = "en-CA-u-hc-h23";

const buildFormatLocaleOptions = (): FormatContextFormatLocaleOption[] => {
  const localLocale = detectBrowserLocale();
  return [
    { id: "us", locale: formatContextDefaultValue.locale, label: "US" },
    { id: "iso", locale: ISO_LOCALE, label: "ISO" },
    { id: "local", locale: localLocale, label: "Local" },
  ];
};

export const FormatProvider = ({ children }: PropsWithChildren) => {
  const timezoneOptions = useMemo(buildTimezoneOptions, []);
  const sanitizeTimezoneId = useCallback(
    (value: unknown): string =>
      typeof value === "string" && timezoneOptions.some((option) => option.id === value)
        ? value
        : (timezoneOptions[0]?.id ?? formatContextDefaultValue.timezoneId),
    [timezoneOptions],
  );
  const [timezoneId, setTimezoneId] = useJsonLocalStorage(TIMEZONE_STORAGE_KEY, sanitizeTimezoneId);
  const formatLocaleOptions = useMemo(buildFormatLocaleOptions, []);
  const sanitizeFormatLocaleId = useCallback(
    (value: unknown): FormatContextFormatLocaleOption["id"] =>
      typeof value === "string" && formatLocaleOptions.some((option) => option.id === value)
        ? (value as FormatContextFormatLocaleOption["id"])
        : formatContextDefaultValue.formatLocaleId,
    [formatLocaleOptions],
  );
  const [formatLocaleId, setFormatLocaleId] = useJsonLocalStorage(FORMAT_LOCALE_STORAGE_KEY, sanitizeFormatLocaleId);

  const selectTimezone = useCallback(
    (optionId: string) => {
      setTimezoneId((current) => (timezoneOptions.some((option) => option.id === optionId) ? optionId : current));
    },
    [timezoneOptions, setTimezoneId],
  );

  const selectFormatLocale = useCallback(
    (optionId: FormatContextFormatLocaleOption["id"]) => {
      setFormatLocaleId((current) =>
        formatLocaleOptions.some((option) => option.id === optionId) ? optionId : current,
      );
    },
    [formatLocaleOptions, setFormatLocaleId],
  );

  const resolvedTimezone =
    timezoneOptions.find((option) => option.id === timezoneId) ??
    timezoneOptions[0] ??
    formatContextDefaultValue.timezoneOptions[0];

  const resolvedFormatLocale =
    formatLocaleOptions.find((option) => option.id === formatLocaleId) ??
    formatLocaleOptions.find((option) => option.id === formatContextDefaultValue.formatLocaleId) ??
    formatContextDefaultValue.formatLocaleOptions[0];

  const timezoneCode = resolvedTimezone?.code ?? formatContextDefaultValue.timezoneCode;
  const timezoneLabel = resolvedTimezone?.label ?? formatContextDefaultValue.timezoneLabel;
  const resolvedTimezoneId = resolvedTimezone?.id ?? formatContextDefaultValue.timezoneId;
  const locale = resolvedFormatLocale?.locale ?? formatContextDefaultValue.locale;
  const resolvedFormatLocaleId = resolvedFormatLocale?.id ?? formatContextDefaultValue.formatLocaleId;

  const value = useMemo(
    () => ({
      timezoneCode,
      timezoneLabel,
      timezoneId: resolvedTimezoneId,
      timezoneOptions,
      selectTimezone,
      formatLocaleId: resolvedFormatLocaleId,
      formatLocaleOptions,
      selectFormatLocale,
      locale,
      currencyCode: formatContextDefaultValue.currencyCode,
    }),
    [
      formatLocaleOptions,
      locale,
      resolvedFormatLocaleId,
      resolvedTimezoneId,
      selectFormatLocale,
      selectTimezone,
      timezoneCode,
      timezoneLabel,
      timezoneOptions,
    ],
  );

  return <FormatContext.Provider value={value}>{children}</FormatContext.Provider>;
};
